<template>
  <div class="ai-report-container">
    <!-- 紧凑型头部 -->
    <div class="d-flex justify-content-between align-items-center px-2 py-3 border-bottom">
      <div class="d-flex align-items-center">
        <b-icon :icon="typeIcon" class="text-primary mr-2" font-scale="1.2"></b-icon>
        <h5 class="mb-0 font-weight-bold">{{ typeName }}</h5>
        <span class="ml-3 text-muted small d-none d-md-inline">共 {{ totalRows }} 份报告</span>
      </div>
      <b-button size="sm" variant="link" class="text-primary p-0" @click="loadReports(1)" :disabled="loading">
        <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''"></b-icon>
        刷新
      </b-button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading && reports.length === 0" class="text-center py-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- 极简紧凑列表 -->
    <div v-else class="compact-list">
      <div 
        v-for="report in reports" 
        :key="report.id" 
        class="report-row border-bottom px-2 py-2"
        @click="goToDetail(report)"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1 overflow-hidden">
            <div class="d-flex align-items-center flex-wrap">
              <span class="report-title font-weight-bold mr-2 text-truncate">{{ report.title }}</span>
              <span class="text-muted x-small">{{ formatTime(report.created_time) }}</span>
            </div>
            
            <div v-if="report.concepts && report.concepts.length > 0" class="mt-1 d-flex flex-wrap">
              <span 
                v-for="(concept, idx) in report.concepts" 
                :key="idx" 
                class="concept-mini-tag mr-2"
              >
                #{{ concept }}
              </span>
            </div>
          </div>
          
          <div class="ml-2 flex-shrink-0">
            <b-icon icon="chevron-right" class="text-light" font-scale="0.8"></b-icon>
          </div>
        </div>
      </div>

      <!-- 紧凑分页器 -->
      <div class="d-flex justify-content-center py-4">
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          class="compact-pagination"
          @change="loadReports"
        ></b-pagination>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!loading && reports.length === 0" class="text-center py-5 text-muted">
      暂无报告
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'WonderAiReport',
  data() {
    return {
      reports: [],
      loading: false,
      currentPage: 1,
      perPage: 20, // 增加每页条数，提高信息密度
      totalRows: 0,
      reportType: this.$route.params.type || 'morning'
    }
  },
  computed: {
    typeName() {
      return this.reportType === 'morning' ? '每日盘前 (AI)' : '每日盘后 (AI)'
    },
    typeIcon() {
      return this.reportType === 'morning' ? 'brightness-alt-high' : 'moon-stars'
    }
  },
  watch: {
    '$route.params.type'(newType) {
      if (newType) {
        this.reportType = newType
        this.currentPage = 1
        this.loadReports()
      }
    }
  },
  mounted() {
    this.loadReports()
  },
  methods: {
    loadReports(page = 1) {
      this.loading = true
      this.currentPage = page
      request_zzquant({
        url: '/v3/ai-report/list',
        method: 'GET',
        params: {
          type: this.reportType,
          page: this.currentPage,
          page_size: this.perPage
        }
      })
      .then(res => {
        const d = res?.data?.data || {}
        this.reports = d.items || []
        this.totalRows = d.total || 0
      })
      .catch(err => {
        console.error('Load reports error:', err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    formatTime(val) {
      if (!val) return ''
      // 只显示月-日 时:分，节省空间
      return val.substring(5, 16)
    },
    goToDetail(item) {
      this.$router.push({
        path: `/wonder/ai-report/detail/${item.id}`,
        query: {
          type: this.reportType
        }
      })
    }
  }
}
</script>

<style scoped>
.ai-report-container {
  max-width: 960px;
  margin: 0 auto;
  background-color: #fff;
  min-height: 100vh;
  border-left: 1px solid #eee;
  border-right: 1px solid #eee;
}

.report-row {
  transition: background-color 0.15s;
  cursor: pointer;
}

.report-row:hover {
  background-color: #f8f9fa;
}

.report-title {
  font-size: 0.95rem;
  color: #333;
  max-width: 80%;
}

.x-small {
  font-size: 0.75rem;
  color: #999;
}

.concept-mini-tag {
  color: #007bff;
  font-size: 0.8rem;
  white-space: nowrap;
}

.concept-mini-tag:hover {
  text-decoration: underline;
}

.compact-pagination >>> .page-link {
  border: 1px solid #eee;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #666;
}

.compact-pagination >>> .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

/* 暗黑模式 */
body.theme-dark .ai-report-container {
  background-color: #1a1a1a;
  color: #eee;
}

body.theme-dark .border-bottom,
body.theme-dark .ai-report-container {
  border-color: #333 !important;
}

body.theme-dark .report-row:hover {
  background-color: #262626;
}

body.theme-dark .report-title {
  color: #ddd;
}

body.theme-dark .concept-mini-tag {
  color: #4facfe;
}
</style>
